import React, { useEffect, useMemo, useState } from 'react';
import { unitLabel } from './unitLabel';
import './PortfolioReport.css';
import './PortfolioCalculator.css';
import {
  Property, LeaseInfo, InstallmentPlan,
  buildRows, useLeases, compactAED, compactEUR, eur, fmtEUR, fmtAEDFull, pct, dateLabel,
} from './portfolioMetrics';
import { SellListing, fetchSellListings, submitEnquiry } from '../portals/shared/sellListings';
import { useLanguage } from './LanguageContext';

/* ================================================================
   EXIT & ENTRY CALCULATOR — Portfolio ▸ Calculator tab.

   Replaces the old Performance tab, whose headline ROI compared a DLD
   market value against a book value and read as a claim about what the
   portfolio "has done". This asks a narrower question the data can
   actually answer:

     SELL — if I sold this unit today at the DLD comparable value, what
            is left after costs, and what is that against the capital I
            have actually put in?
     BUY  — what could that capital go into instead, and what does the
            same arithmetic look like on a discounted Dubai Sell unit?

   SELL is answered ONE UNIT AT A TIME. There is deliberately no
   portfolio rollup: adding up 19 units of different vintages, payment
   states and comparable-sale depth produces a single number that
   describes none of them, which is the failure mode of the tab this
   replaced.

   Rules carried over from the rest of the Portfolio tab:
     • A market value is NEVER synthesised. A unit with no DLD
       comparables says so and calculates nothing, rather than being
       quietly valued at its purchase price.
     • Capital invested is what has actually been PAID (installment plan
       milestones marked paid), not the contract price — an off-plan
       owner's return is on their equity, not the developer's.
     • Every cost assumption is on screen and editable. Nothing is
       deducted that the owner cannot see and change.
   ================================================================ */

interface Props {
  properties: Property[];
  investorId?: string;
  investorName?: string;
  apiUrl?: string;
  dldData?: any;
  /** Sample leases for the showcase — see PortfolioReport.leasesOverride. */
  leasesOverride?: Record<string, LeaseInfo>;
  /**
   * Sample Dubai Sell inventory for the login-free showcase (portals/demo),
   * which passes no apiUrl and must make no network call. Absent in the app,
   * where the listings are fetched.
   */
  listingsOverride?: SellListing[];
}

// ---------------------------------------------------------------- assumptions
/**
 * Dubai transaction costs — the figures this desk actually charges. Defaults,
 * not constants: every one of them is a field on screen.
 */
const DEFAULTS = {
  transferPct: 4,      // DLD transfer fee, 4% of the transaction
  agencyPct: 2.5,      // agency fee, 2.5%
  progressionAed: 4000, // sales progression fee, flat AED per transaction
  priceAdjPct: 0,      // sale price vs the DLD market value, -20% … +20%
};

type Assumptions = typeof DEFAULTS;

// ---------------------------------------------------------------- maths
export interface ExitResult {
  /** what a buyer pays */
  salePrice: number;
  agencyFee: number;
  transferFee: number;
  otherCosts: number;
  /** still owed to the developer on an off-plan unit */
  balanceDue: number;
  /** what reaches the owner */
  netProceeds: number;
  /** cash the owner has actually put in */
  capitalIn: number;
  gain: number;
  /** gain over capital actually paid */
  roiPct: number;
  /** compounded to a yearly rate, null when held under six months */
  annualisedPct: number | null;
  years: number;
}

export function computeExit(
  salePrice: number,
  capitalIn: number,
  balanceDue: number,
  years: number,
  a: Assumptions,
): ExitResult {
  const agencyFee = salePrice * (a.agencyPct / 100);
  const transferFee = salePrice * (a.transferPct / 100);
  const netProceeds = salePrice - agencyFee - transferFee - a.progressionAed - balanceDue;
  const gain = netProceeds - capitalIn;
  const roiPct = capitalIn > 0 ? (gain / capitalIn) * 100 : 0;

  // Annualising a six-week hold produces a triple-digit number that means
  // nothing. Below half a year it is simply not shown.
  let annualisedPct: number | null = null;
  if (years >= 0.5 && capitalIn > 0 && netProceeds > 0) {
    annualisedPct = (Math.pow(netProceeds / capitalIn, 1 / years) - 1) * 100;
  }

  return {
    salePrice, agencyFee, transferFee, otherCosts: a.progressionAed,
    balanceDue, netProceeds, capitalIn, gain, roiPct, annualisedPct, years,
  };
}

export interface EntryResult {
  askingPrice: number;
  transferFee: number;
  agencyFee: number;
  otherCosts: number;
  /** total cash to acquire */
  cashIn: number;
  /** the value it is being bought below */
  marketValue: number;
  /** what a resale at market value leaves, after the exit agency fee */
  resaleNet: number;
  gain: number;
  roiPct: number;
}

export function computeEntry(askingPrice: number, marketValue: number, a: Assumptions): EntryResult {
  const transferFee = askingPrice * (a.transferPct / 100);
  const agencyFee = askingPrice * (a.agencyPct / 100);
  const cashIn = askingPrice + transferFee + agencyFee + a.progressionAed;
  // On the way out the transfer fee is the next buyer's — only the agency
  // commission and the admin costs come off the seller again.
  const resaleNet = marketValue - marketValue * (a.agencyPct / 100) - a.progressionAed;
  const gain = resaleNet - cashIn;
  return {
    askingPrice, transferFee, agencyFee, otherCosts: a.progressionAed,
    cashIn, marketValue, resaleNet, gain,
    roiPct: cashIn > 0 ? (gain / cashIn) * 100 : 0,
  };
}

/** Years held, from the earliest date the record carries. */
function yearsHeld(unit: Property): number {
  const iso = unit.purchaseDate || unit.otpDate || unit.createdAt;
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 0;
  return Math.max(0, (Date.now() - t) / (365.25 * 24 * 3600 * 1000));
}

const signedPct = (value: number) => `${value >= 0 ? '+' : ''}${pct(value)}`;

// ---------------------------------------------------------------- small parts
const Stepper: React.FC<{
  value: number;
  onChange: (v: number) => void;
  step: number;
  min: number;
  max: number;
  suffix?: string;
  label: string;
}> = ({ value, onChange, step, min, max, suffix, label }) => {
  const { t } = useLanguage();
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v * 100) / 100));
  return (
    <span className="pc-step">
      <button type="button" aria-label={`${t('pcalcDecrease')} ${label}`} onClick={() => onChange(clamp(value - step))}>−</button>
      <input
        type="number"
        inputMode="decimal"
        aria-label={label}
        value={value}
        step={step}
        onChange={e => {
          const v = parseFloat(e.target.value);
          onChange(isNaN(v) ? min : clamp(v));
        }}
      />
      <button type="button" aria-label={`${t('pcalcIncrease')} ${label}`} onClick={() => onChange(clamp(value + step))}>+</button>
      {suffix ? <span style={{ marginLeft: 8, fontSize: '1rem', fontWeight: 700 }}>{suffix}</span> : null}
    </span>
  );
};

// ---------------------------------------------------------------- component
const PortfolioCalculator: React.FC<Props> = ({
  properties, investorId, investorName, apiUrl, dldData, leasesOverride, listingsOverride,
}) => {
  const { t } = useLanguage();
  // DLD confidence arrives as HIGH / MEDIUM / LOW. Translate the three we know
  // and fall back to the raw word, so an unexpected grade still reads.
  const confidenceLabel = (c: string) => {
    const key = c.toLowerCase().replace(/_/g, ' ').trim();
    if (key === 'high') return t('pcalcConfHigh');
    if (key === 'medium') return t('pcalcConfMedium');
    if (key === 'low') return t('pcalcConfLow');
    return key;
  };
  const [mode, setMode] = useState<'sell' | 'buy'>('sell');
  const [currency, setCurrency] = useState<'EUR' | 'AED'>('EUR');
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [a, setA] = useState<Assumptions>(DEFAULTS);
  // SELL is a per-unit calculation: one unit at a time, never a portfolio
  // rollup. Summing 19 units of different vintages, payment states and
  // comparable-sale depth produced a single figure that described none of
  // them — which is exactly what was wrong with the tab this replaced.
  const [sellUnitId, setSellUnitId] = useState<string | null>(null);

  const [plans, setPlans] = useState<InstallmentPlan[]>([]);
  const [listings, setListings] = useState<SellListing[] | null>(listingsOverride || null);
  const [listingsError, setListingsError] = useState<string | null>(null);
  const [matchedOnly, setMatchedOnly] = useState(false);

  const leases = useLeases(investorId, leasesOverride);
  const units = useMemo(() => properties || [], [properties]);
  const rows = useMemo(() => buildRows(units, dldData, leases), [units, dldData, leases]);

  const money = (aed: number) => (currency === 'EUR' ? compactEUR(eur(aed)) : compactAED(aed));
  const moneyFull = (aed: number) =>
    currency === 'EUR' ? fmtEUR(Math.round(eur(aed))) : `AED ${fmtAEDFull(aed)}`;
  const signed = (aed: number) => `${aed >= 0 ? '+' : '−'}${moneyFull(Math.abs(aed))}`;

  // ---- payment plans: how much of each contract has actually been paid
  useEffect(() => {
    if (!investorId || !apiUrl) return;   // showcase / logged-out: nothing to fetch
    const token = localStorage.getItem('authToken');
    // PortfolioView hands down an apiUrl that already ends in /api; other
    // callers pass the bare service URL. Normalise before re-adding it.
    const base = apiUrl.replace(/\/api\/?$/, '');
    let alive = true;
    fetch(`${base}/api/installment-plans/${encodeURIComponent(investorId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (alive) setPlans(Array.isArray(d) ? d : []); })
      .catch(() => { /* no plan on file — the unit falls back to fully paid */ });
    return () => { alive = false; };
  }, [investorId, apiUrl]);

  // ---- Dubai Sell inventory, for the buy side
  useEffect(() => {
    if (listingsOverride) return;         // showcase supplies its own, makes no call
    if (mode !== 'buy' || listings) return;
    if (!apiUrl) return;                  // login-free tour: no network from this subtree
    let alive = true;
    fetchSellListings(50)
      .then(r => { if (alive) setListings(r.listings); })
      .catch(e => { if (alive) setListingsError(e?.message || 'Listings unavailable'); });
    return () => { alive = false; };
  }, [mode, listings, apiUrl, listingsOverride]);

  // ---- capital actually paid per unit
  const paidByUnit = useMemo(() => {
    const map = new Map<string, number>();
    plans.forEach(p => {
      const milestones = p.milestones || [];
      if (!p.unitId || milestones.length === 0) return;
      const paid = milestones
        .filter(m => m.status === 'paid')
        .reduce((s, m) => s + (m.amount || 0), 0);
      map.set(p.unitId, paid);
    });
    return map;
  }, [plans]);

  // ---- one exit scenario per unit
  const exits = useMemo(() => {
    return rows
      .map(r => {
        const dld = r.dld || {};
        const salePrice = Math.max(0, r.marketValue * (1 + a.priceAdjPct / 100));
        const paid = paidByUnit.get(r.unit.id) || 0;
        // No plan on file, or a plan showing nothing paid, means we cannot
        // prove partial payment — treat the unit as fully paid and say so.
        const hasPartial = paid > 0 && paid < r.purchase;
        const capitalIn = hasPartial ? paid : r.purchase;
        const balanceDue = hasPartial ? r.purchase - paid : 0;
        return {
          row: r,
          hasPartial,
          confidence: String(dld.estimateConfidence || dld.confidence || ''),
          comps: dld.comparablesCount || 0,
          low: dld.dldMarketValueLow || null,
          high: dld.dldMarketValueHigh || null,
          result: computeExit(salePrice, capitalIn, balanceDue, yearsHeld(r.unit), a),
        };
      });
  }, [rows, paidByUnit, a]);

  /** The unit on screen. Defaults to the first one DLD can actually value. */
  const selected = useMemo(() => {
    if (sellUnitId) return exits.find(e => e.row.unit.id === sellUnitId) || null;
    return exits.find(e => e.row.hasDldValue && e.row.purchase > 0) || exits[0] || null;
  }, [exits, sellUnitId]);

  // ---- what the owner already holds, for matching Dubai Sell stock against
  const ownedPlaces = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => {
      [r.unit.location, r.unit.project].forEach(v => {
        const s = (v || '').toLowerCase().trim();
        if (s.length >= 4) set.add(s);
      });
    });
    return Array.from(set);
  }, [rows]);

  const ownedBeds = useMemo(() => {
    const set = new Set<number>();
    rows.forEach(r => {
      const b = Number(r.unit.bedrooms);
      if (Number.isFinite(b) && b > 0) set.add(b);
    });
    return set;
  }, [rows]);

  const buyDeals = useMemo(() => {
    if (!listings) return [];

    /** Why this listing is relevant to this owner — null when it is not. */
    const matchReason = (l: SellListing): string | null => {
      const loc = (l.location || '').toLowerCase().trim();
      if (loc.length >= 4 && ownedPlaces.some(p => p.includes(loc) || loc.includes(p))) {
        return t('pcalcSameArea');
      }
      const beds = Number(l.bedrooms);
      if (Number.isFinite(beds) && ownedBeds.has(beds)) return `${t('pcalcSameSize')} ${beds} B/R`;
      return null;
    };

    return listings
      // Only a listing carrying its own market reference can be scored — the
      // discount and the resale figure both hang off it.
      .filter(l => l.isDistress && l.askingPrice && l.marketValue && l.marketValue > 0)
      .map(l => ({
        listing: l,
        match: matchReason(l),
        result: computeEntry(l.askingPrice as number, l.marketValue as number, a),
      }))
      .filter(d => (matchedOnly ? !!d.match : true))
      .sort((x, y) => (x.match ? 0 : 1) - (y.match ? 0 : 1) || y.result.roiPct - x.result.roiPct);
  }, [listings, a, matchedOnly, ownedPlaces, ownedBeds]);

  // ---------------------------------------------------------------- render
  const toolbar = (
    <div className="pr-toolbar" style={{ padding: '0 0 14px' }}>
      <span className="pr-toggle">
        {(['EUR', 'AED'] as const).map(c => (
          <button key={c} data-on={currency === c} onClick={() => setCurrency(c)}>{c}</button>
        ))}
      </span>
      {mode === 'buy' && (
        <span className="pr-toggle">
          <button data-on={!matchedOnly} onClick={() => setMatchedOnly(false)}>{t('pcalcAllDeals')}</button>
          <button data-on={matchedOnly} onClick={() => setMatchedOnly(true)}>{t('pcalcLikeMine')}</button>
        </span>
      )}
    </div>
  );

  const assumptions = (
    <>
      <button
        type="button"
        className="pc-assum-toggle"
        data-open={assumptionsOpen}
        onClick={() => setAssumptionsOpen(o => !o)}
      >
        <span>
          <b>{t('pcalcCostAssumptions')}</b>
          <span className="pc-field-note">
            {/* the AED figure, not the display currency — it is what the field below edits */}
            {a.transferPct}% DLD · {a.agencyPct}% {t('pcalcAgencyShort')} · AED {fmtAEDFull(a.progressionAed)} {t('pcalcProgressionShort')}
            {mode === 'sell' && a.priceAdjPct !== 0
              ? ` · ${t('pcalcSalePriceShort')} ${a.priceAdjPct > 0 ? '+' : '−'}${Math.abs(a.priceAdjPct)}%`
              : ''}
          </span>
        </span>
        <span aria-hidden="true" style={{ fontSize: '1.25rem' }}>{assumptionsOpen ? '−' : '+'}</span>
      </button>

      {assumptionsOpen && (
        <div className="pc-assum">
          {mode === 'sell' && (
            <div className="pc-field">
              <span className="pc-field-lab">
                {t('pcalcSaleVsMarket')}
                <span className="pc-field-note">{t('pcalcSaleVsMarketNote')}</span>
              </span>
              <Stepper label={t('pcalcSalePriceAdj')} value={a.priceAdjPct} min={-20} max={20} step={1} suffix="%"
                onChange={v => setA(s => ({ ...s, priceAdjPct: v }))} />
            </div>
          )}
          <div className="pc-field">
            <span className="pc-field-lab">
              {t('pcalcDldFee')}
              <span className="pc-field-note">{t('pcalcDldFeeNote')}</span>
            </span>
            <Stepper label={t('pcalcDldFee')} value={a.transferPct} min={0} max={10} step={0.5} suffix="%"
              onChange={v => setA(s => ({ ...s, transferPct: v }))} />
          </div>
          <div className="pc-field">
            <span className="pc-field-lab">
              {t('pcalcAgencyFee')}
              <span className="pc-field-note">{t('pcalcAgencyFeeNote')}</span>
            </span>
            <Stepper label={t('pcalcAgencyFee')} value={a.agencyPct} min={0} max={10} step={0.5} suffix="%"
              onChange={v => setA(s => ({ ...s, agencyPct: v }))} />
          </div>
          <div className="pc-field">
            <span className="pc-field-lab">
              {t('pcalcProgressionFee')}
              <span className="pc-field-note">{t('pcalcProgressionFeeNote')}</span>
            </span>
            <Stepper label={t('pcalcProgressionFee')} value={a.progressionAed} min={0} max={100000} step={500}
              onChange={v => setA(s => ({ ...s, progressionAed: v }))} />
          </div>
          <button type="button" className="pc-reset" onClick={() => setA(DEFAULTS)}>
            {t('pcalcResetCosts')}
          </button>
        </div>
      )}
    </>
  );

  // ------------------------------------------------------------- SELL
  const renderSell = () => {
    if (!selected) {
      return <p className="pr-empty">{t('pcalcAddProperty')}</p>;
    }

    const e = selected;
    const r = e.result;
    const unit = e.row.unit;
    const valued = e.row.hasDldValue && e.row.purchase > 0;
    // How the current market value sits against what was paid, before any
    // selling cost. Shown on the purchase-price row because it is the reason
    // the gain further down is positive or negative.
    const valueShift = e.row.purchase > 0
      ? ((e.row.marketValue - e.row.purchase) / e.row.purchase) * 100
      : 0;

    // The unit picker. Every unit is listed, valued or not — a unit missing
    // from the list reads as a unit we forgot, and the honest answer for one
    // DLD cannot value yet belongs on screen, not hidden.
    const picker = (
      <label className="pc-picker">
        <span className="pr-label">{t('pcalcUnit')}</span>
        <select
          className="pc-select"
          value={unit.id}
          onChange={ev => setSellUnitId(ev.target.value)}
        >
          {exits.map(x => (
            <option key={x.row.unit.id} value={x.row.unit.id}>
              {unitLabel(x.row.unit.name || x.row.unit.project, x.row.unit.unitNumber) || t('pcalcUnit')}
              {x.row.hasDldValue ? '' : ` — ${t('pcalcNotYetValued')}`}
            </option>
          ))}
        </select>
      </label>
    );

    if (!valued) {
      return (
        <>
          {picker}
          <p className="pr-empty">
            {unit.name || unit.project || t('pcalcThisUnit')} {t('pcalcCannotValue')}
          </p>
        </>
      );
    }

    return (
      <>
        {picker}

        <div className="pc-head">
          <p className="pr-kicker">{t('pcalcIfSoldToday')}</p>
          <p className="pc-head-fig pr-serif">{signed(r.gain)}</p>
          <p className="pc-head-sub">
            {signedPct(r.roiPct)} {t('pcalcOn')} {moneyFull(r.capitalIn)} {t('pcalcOfCapitalPaidIn')}
            {r.annualisedPct != null ? ` · ${signedPct(r.annualisedPct)} ${t('pcalcAYear')}` : ''}
          </p>
          <div className="pc-head-split">
            <div>
              <div className="pc-head-cell-lab">{t('pcalcSalePrice')}</div>
              <div className="pc-head-cell-val pr-serif">{money(r.salePrice)}</div>
            </div>
            <div>
              <div className="pc-head-cell-lab">{t('pcalcNetToYou')}</div>
              <div className="pc-head-cell-val pr-serif">{money(r.netProceeds)}</div>
            </div>
          </div>
        </div>

        <div className="pr-panel">
          <div className="pr-panel-head">
            <div>
              <p className="pr-label">{unit.name || unit.project || t('pcalcUnit')}</p>
              <p className="pc-sub" style={{ marginTop: 4 }}>
                {[unit.unitNumber ? `${t('pcalcUnit')} ${unit.unitNumber}` : null,
                  unit.bedrooms ? `${unit.bedrooms} B/R` : null,
                  unit.type,
                  unit.location].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>

          <div className="pc-flow">
            <div className="pc-flow-row" data-kind="context">
              <span>
                {t('pcalcPurchasePrice')}
                <span className="pc-sub" style={{ display: 'block' }}>
                  {unit.purchaseDate ? `${t('pcalcBought')} ${dateLabel(unit.purchaseDate)}` : t('pcalcContractPrice')}
                  {/* the single most useful comparison on this screen: it is why
                      the gain below is positive or negative before any fee */}
                  {` · ${t('pcalcMarketToday')} ${pct(Math.abs(valueShift))} ${valueShift >= 0 ? t('pcalcAboveIt') : t('pcalcBelowIt')}`}
                </span>
              </span>
              <span>{moneyFull(e.row.purchase)}</span>
            </div>
            <div className="pc-flow-row">
              <span>
                {t('pcalcSalePrice')}
                <span className="pc-sub" style={{ display: 'block' }}>
                  {t('pcalcFromValuationTab')}
                  {a.priceAdjPct !== 0
                    ? `, ${t('pcalcAdjusted')} ${a.priceAdjPct > 0 ? '+' : '−'}${Math.abs(a.priceAdjPct)}%`
                    : ''}
                  {e.low && e.high ? ` · ${t('pcalcDldRange')} ${money(e.low)} – ${money(e.high)}` : ''}
                </span>
              </span>
              <span>{moneyFull(r.salePrice)}</span>
            </div>
            {a.transferPct > 0 && (
              <div className="pc-flow-row" data-kind="minus">
                <span>{t('pcalcDldFee')} {a.transferPct}%</span><span>−{moneyFull(r.transferFee)}</span>
              </div>
            )}
            <div className="pc-flow-row" data-kind="minus">
              <span>{t('pcalcAgencyFee')} {a.agencyPct}%</span><span>−{moneyFull(r.agencyFee)}</span>
            </div>
            <div className="pc-flow-row" data-kind="minus">
              <span>{t('pcalcProgressionFee')}</span><span>−{moneyFull(r.otherCosts)}</span>
            </div>
            {r.balanceDue > 0 && (
              <div className="pc-flow-row" data-kind="minus">
                <span>
                  {t('pcalcBalanceOwed')}
                  <span className="pc-sub" style={{ display: 'block' }}>
                    {t('pcalcBalanceNote')}
                  </span>
                </span>
                <span>−{moneyFull(r.balanceDue)}</span>
              </div>
            )}
            <div className="pc-flow-row" data-kind="total">
              <span>{t('pcalcNetProceeds')}</span><span>{moneyFull(r.netProceeds)}</span>
            </div>
            <div className="pc-flow-row" data-kind="minus">
              <span>
                {t('pcalcCapitalPaidIn')}
                <span className="pc-sub" style={{ display: 'block' }}>
                  {e.hasPartial
                    ? `${t('pcalcPaidInstalments')} ${moneyFull(e.row.purchase)}`
                    : t('pcalcNoPlan')}
                </span>
              </span>
              <span>−{moneyFull(r.capitalIn)}</span>
            </div>
            <div className="pc-flow-row" data-kind="gain">
              <span>{t('pcalcGainOnExit')}</span><span>{signed(r.gain)}</span>
            </div>
            <div className="pc-flow-row">
              <span>{t('pcalcReturnOnCapital')}</span>
              <span>
                {signedPct(r.roiPct)}
                {r.annualisedPct != null ? ` · ${signedPct(r.annualisedPct)} ${t('pcalcPerYearShort')}` : ''}
              </span>
            </div>
          </div>

          <p className="pr-hint">
            {t('pcalcValuedFrom')} {e.comps} {e.comps === 1 ? t('pcalcDldSaleOne') : t('pcalcDldSaleMany')}
            {e.confidence ? ` · ${confidenceLabel(e.confidence)} ${t('pcalcConfidence')}` : ''}.
            {' '}{t('pcalcEstimateNote')}
          </p>
        </div>
      </>
    );
  };

  // ------------------------------------------------------------- BUY
  const renderBuy = () => {
    if (listingsError) {
      return <p className="pr-empty">{t('pcalcSellUnavailable')}</p>;
    }
    if (!listings) {
      // No apiUrl and no override means nothing will ever arrive — say so
      // rather than spinning on "Loading" forever.
      return apiUrl
        ? <p className="pr-empty">{t('pcalcLoadingInventory')}</p>
        : <p className="pr-empty">{t('pcalcSignInToSee')}</p>;
    }
    if (buyDeals.length === 0) {
      return (
        <p className="pr-empty">
          {matchedOnly
            ? t('pcalcNoMatch')
            : t('pcalcNoDeals')}
        </p>
      );
    }

    return (
      <>
        <div className="pr-note" style={{ marginBottom: 16 }}>
          {t('pcalcBuyNoteA')}{' '}
          <b>{t('pcalcBuyNoteBold')}</b> {t('pcalcBuyNoteB')}
        </div>
        {buyDeals.map(d => (
          <BuyCard
            key={d.listing.id}
            deal={d}
            moneyFull={moneyFull}
            signed={signed}
            agencyPct={a.agencyPct}
            transferPct={a.transferPct}
            investorName={investorName}
            investorId={investorId}
            canEnquire={!!apiUrl}
          />
        ))}
      </>
    );
  };

  return (
    <div className="pr-root pr-embedded">
      <div className="pc-modes">
        <button className="pc-mode" data-on={mode === 'sell'} onClick={() => setMode('sell')}>
          <span>
            <span className="pc-mode-t">{t('pcalcSell')}</span>
            <span className="pc-mode-s">{t('pcalcSellSub')}</span>
          </span>
        </button>
        <button className="pc-mode" data-on={mode === 'buy'} onClick={() => setMode('buy')}>
          <span>
            <span className="pc-mode-t">{t('pcalcBuy')}</span>
            <span className="pc-mode-s">{t('pcalcBuySub')}</span>
          </span>
        </button>
      </div>

      {toolbar}
      {assumptions}
      {mode === 'sell' ? renderSell() : renderBuy()}
    </div>
  );
};

// ---------------------------------------------------------------- buy card
interface BuyCardProps {
  deal: { listing: SellListing; match: string | null; result: EntryResult };
  moneyFull: (aed: number) => string;
  signed: (aed: number) => string;
  agencyPct: number;
  transferPct: number;
  investorName?: string;
  investorId?: string;
  canEnquire: boolean;
}

const BuyCard: React.FC<BuyCardProps> = ({
  deal, moneyFull, signed, agencyPct, transferPct, investorName, investorId, canEnquire,
}) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const l = deal.listing;
  const r = deal.result;
  // Investor records are keyed by email in this app, so investorId is the
  // address to reach them on — but only when it actually looks like one.
  const email = investorId && investorId.includes('@') ? investorId : '';
  const title = l.title || [l.bedrooms ? `${l.bedrooms} B/R` : null, l.propertyType, l.location]
    .filter(Boolean).join(' · ') || t('pcalcDubaiSellUnit');

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      await submitEnquiry({
        listing: l,
        name: investorName || 'Asset Desk owner',
        email,
        message: 'Sent from the Asset Desk portfolio calculator.',
      });
      setSent(true);
      setConfirming(false);
    } catch (e: any) {
      setError(e?.message || t('pcalcCouldNotSend'));
    }
    setSending(false);
  };

  return (
    <div className="pc-card">
      {l.photoUrl && <img className="pc-card-photo" src={l.photoUrl} alt="" loading="lazy" />}
      <div className="pc-card-body">
        <div className="pc-tags">
          {l.discountPercent ? <span className="pc-tag">{l.discountPercent}% {t('pcalcBelowMarket')}</span> : null}
          {deal.match ? <span className="pc-tag" data-tone="match">{deal.match}</span> : null}
        </div>
        <div className="pr-tr-name">{title}</div>
        <div className="pr-tr-sub">
          {[l.location, l.bedrooms ? `${l.bedrooms} B/R` : null,
            l.sizeSqft ? `${Math.round(l.sizeSqft).toLocaleString('en-AE')} sqft` : null]
            .filter(Boolean).join(' · ')}
        </div>

        <div className="pc-flow" style={{ marginTop: 12 }}>
          <div className="pc-flow-row"><span>{t('pcalcAskingPrice')}</span><span>{moneyFull(r.askingPrice)}</span></div>
          <div className="pc-flow-row"><span>{t('pcalcMarketValue')}</span><span>{moneyFull(r.marketValue)}</span></div>
        </div>

        {open && (
          <div className="pc-flow" style={{ marginTop: 4 }}>
            <div className="pc-flow-row" data-kind="minus">
              <span>{t('pcalcDldFee')} {transferPct}%</span><span>−{moneyFull(r.transferFee)}</span>
            </div>
            <div className="pc-flow-row" data-kind="minus">
              <span>{t('pcalcAgencyFee')} {agencyPct}%</span><span>−{moneyFull(r.agencyFee)}</span>
            </div>
            <div className="pc-flow-row" data-kind="minus">
              <span>{t('pcalcProgressionFee')}</span><span>−{moneyFull(r.otherCosts)}</span>
            </div>
            <div className="pc-flow-row" data-kind="total"><span>{t('pcalcCashToAcquire')}</span><span>{moneyFull(r.cashIn)}</span></div>
            <div className="pc-flow-row">
              <span>
                {t('pcalcResaleAtMarket')}
                <span className="pc-sub" style={{ display: 'block' }}>{t('pcalcResaleNoteA')} {agencyPct}% {t('pcalcResaleNoteB')}</span>
              </span>
              <span>{moneyFull(r.resaleNet)}</span>
            </div>
            <div className="pc-flow-row" data-kind="gain"><span>{t('pcalcGainOnResale')}</span><span>{signed(r.gain)}</span></div>
          </div>
        )}

        <button type="button" className="pc-cta" data-variant="quiet" onClick={() => setOpen(o => !o)}>
          {open ? t('pcalcHideNumbers') : t('pcalcShowNumbers')}
        </button>

        {canEnquire && !sent && (
          confirming ? (
            <div className="pc-confirm">
              <p className="pc-sub" style={{ marginBottom: 10 }}>
                {t('pcalcEnquiryIntroA')}{email ? ` ${t('pcalcEnquiryAnd')} ${email}` : ''} {t('pcalcEnquiryIntroB')}
              </p>
              {error && <p className="pc-sub" style={{ color: '#8f2f22', marginBottom: 10 }}>{error}</p>}
              <button type="button" className="pc-cta" style={{ marginTop: 0 }} disabled={sending} onClick={send}>
                {sending ? t('pcalcSending') : t('pcalcSendEnquiry')}
              </button>
              <button type="button" className="pc-cta" data-variant="quiet" disabled={sending}
                onClick={() => { setConfirming(false); setError(null); }}>
                {t('pcalcCancel')}
              </button>
            </div>
          ) : (
            <button type="button" className="pc-cta" onClick={() => setConfirming(true)}>
              {t('pcalcRegisterInterest')}
            </button>
          )
        )}
        {sent && (
          <p className="pc-sub" style={{ marginTop: 12, color: 'var(--pr-green)', fontWeight: 700 }}>
            {t('pcalcEnquirySent')}
          </p>
        )}
      </div>

      <div className="pc-roi-strip">
        <span className="pr-label">{t('pcalcReturnIfResold')}</span>
        <b className="pr-serif">{r.roiPct >= 0 ? '+' : ''}{r.roiPct.toFixed(1)}%</b>
      </div>
    </div>
  );
};

export default PortfolioCalculator;
